import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { copy, PRODUCT_LINES } from "@wheels-crm/shared-types";
import type { OrderStatus, ProductLine, RejectReason } from "@wheels-crm/shared-types";
import { useOrders } from "../hooks/useOrders";
import { supabase } from "../lib/supabaseClient";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { ResizableTable, type TableColumn } from "../components/orders/ResizableTable";
import { StatusSelect } from "../components/orders/StatusSelect";
import { HotToggle } from "../components/orders/HotToggle";
import { ReminderBadge, pickActiveReminder } from "../components/orders/ReminderBadge";

const COLUMNS: TableColumn[] = [
  { key: "date", label: copy.table.date, width: 100 },
  { key: "status", label: copy.table.status, width: 150 },
  { key: "client", label: copy.table.client, width: 180 },
  { key: "phone", label: copy.table.phone, width: 150 },
  { key: "carWheel", label: copy.table.carWheel, width: 220 },
  { key: "comment", label: copy.table.comment, width: 260 },
];

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

export function OrdersPage() {
  const { orders, loading } = useOrders();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrderStatus | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const productLine: ProductLine =
    searchParams.get("line") === "steering_wheels" ? "steering_wheels" : "wheels";

  function setProductLine(line: ProductLine) {
    setStatusFilter(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("line", line);
      return next;
    });
  }

  const lineOrders = useMemo(
    () => orders.filter((o) => o.product_line === productLine),
    [orders, productLine],
  );

  const counts = useMemo(() => {
    const c: Record<OrderStatus, number> = { new: 0, in_progress: 0, done: 0, rejected: 0 };
    for (const o of lineOrders) c[o.status]++;
    return c;
  }, [lineOrders]);

  // Same phone appearing on multiple orders (within this product line) usually means the
  // same client called/wrote in more than once — flag it so the operator doesn't dial twice
  // thinking these are two different people (owner's explicit request).
  const phoneCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of lineOrders) {
      m.set(o.client.phone, (m.get(o.client.phone) ?? 0) + 1);
    }
    return m;
  }, [lineOrders]);

  function toggleStatusFilter(status: OrderStatus) {
    setStatusFilter((current) => (current === status ? null : status));
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return lineOrders.filter((o) => {
      if (statusFilter && o.status !== statusFilter) return false;
      if (!q) return true;
      return (
        o.client.name.toLowerCase().includes(q) ||
        o.client.phone.toLowerCase().includes(q) ||
        (o.car_brand ?? "").toLowerCase().includes(q)
      );
    });
  }, [lineOrders, query, statusFilter]);

  async function handleStatusChange(orderId: string, status: OrderStatus, rejectReason?: RejectReason) {
    await supabase
      .from("orders")
      .update({ status, reject_reason: status === "rejected" ? rejectReason ?? null : null })
      .eq("id", orderId);
  }

  async function toggleHot(orderId: string, current: boolean) {
    await supabase.from("orders").update({ is_hot: !current }).eq("id", orderId);
  }

  return (
    <div>
      <div className="page-header">
        <h1>{copy.nav.orders}</h1>
        <Link to={`/orders/new?line=${productLine}`}>
          <Button variant="primary">{copy.newOrderButton}</Button>
        </Link>
      </div>

      <div className="product-line-switch">
        {PRODUCT_LINES.map((line) => (
          <button
            key={line}
            type="button"
            className={`product-line-tab ${productLine === line ? "product-line-tab-active" : ""}`}
            onClick={() => setProductLine(line)}
          >
            {copy.productLine[line]}
          </button>
        ))}
      </div>

      <div className="status-summary">
        <button
          type="button"
          className={`status-chip status-chip-new ${statusFilter === "new" ? "status-chip-selected" : ""}`}
          onClick={() => toggleStatusFilter("new")}
        >
          {copy.orderStatus.new}: {counts.new}
        </button>
        <button
          type="button"
          className={`status-chip status-chip-in_progress ${statusFilter === "in_progress" ? "status-chip-selected" : ""}`}
          onClick={() => toggleStatusFilter("in_progress")}
        >
          {copy.orderStatus.in_progress}: {counts.in_progress}
        </button>
        <button
          type="button"
          className={`status-chip status-chip-done ${statusFilter === "done" ? "status-chip-selected" : ""}`}
          onClick={() => toggleStatusFilter("done")}
        >
          {copy.orderStatus.done}: {counts.done}
        </button>
        <button
          type="button"
          className={`status-chip status-chip-rejected ${statusFilter === "rejected" ? "status-chip-selected" : ""}`}
          onClick={() => toggleStatusFilter("rejected")}
        >
          {copy.orderStatus.rejected}: {counts.rejected}
        </button>
      </div>

      <div className="search-bar">
        <Input
          placeholder={copy.clients.searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {loading ? (
        <p>Завантаження…</p>
      ) : filtered.length === 0 ? (
        <p className="empty-state">{copy.emptyState}</p>
      ) : (
        <ResizableTable columns={COLUMNS}>
          {filtered.map((o) => {
            const activeReminder = pickActiveReminder(o.reminders);
            const phoneCount = phoneCounts.get(o.client.phone) ?? 0;
            return (
              <tr
                key={o.id}
                className={`order-row row-status-${o.status} ${o.is_hot ? "row-hot" : ""}`}
                onClick={() => navigate(`/orders/${o.id}`)}
              >
                <td>{formatDate(o.created_at)}</td>
                <td>
                  <StatusSelect
                    value={o.status}
                    onChange={(status, rejectReason) => handleStatusChange(o.id, status, rejectReason)}
                  />
                </td>
                <td>
                  <HotToggle active={o.is_hot} onToggle={() => toggleHot(o.id, o.is_hot)} />
                  {o.client.name}
                </td>
                <td>
                  {o.client.phone}
                  {phoneCount > 1 && <span className="phone-dup-badge"> ({phoneCount})</span>}
                </td>
                <td>
                  {[o.size_spec, o.car_brand, o.car_year].filter(Boolean).join(" ")}
                  <br />
                  <ReminderBadge reminder={activeReminder} />
                </td>
                <td>{o.notes}</td>
              </tr>
            );
          })}
        </ResizableTable>
      )}
    </div>
  );
}
